import React, { HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { NameType } from './Avatar';

export interface NameAvatarProps extends HTMLAttributes<HTMLDivElement> {
  size?: string;
  sizeStyles?: string;
  name: NameType;
  backgroundColor?: string;
  staticBackground?: boolean;
}

export const NameAvatar = ({
  className,
  size,
  sizeStyles,
  name,
  backgroundColor,
  staticBackground,
  ...rest
}: NameAvatarProps) => {
  const backgroundColors = [
    'bg-thryv-sapphire-500',
    'bg-thryv-sapphire-300',
    'bg-thryv-sapphire-400',
    'bg-thryv-sapphire-600',
    'bg-thryv-orange-500',
    'bg-thryv-orange-400',
    'bg-thryv-orange-600',
    'bg-thryv-cornflower-500',
    'bg-thryv-cornflower-400',
    'bg-thryv-cornflower-600',
  ];

  const [fontSize, setFontSize] = useState<string | undefined>();

  const getRandomBackgroundColor = (colorsList: string[]) => {
    const randomNumber = Math.floor(Math.random() * (colorsList.length - 1));
    return colorsList[randomNumber];
  };

  const memoizedValue = useMemo(
    () => getRandomBackgroundColor(backgroundColors),
    []
  );

  const getInitials = (nameObj: NameType) => {
    const firstNameArr = nameObj?.firstName?.split('');
    const lastNameArr = nameObj?.lastName?.split('');

    if (!firstNameArr) {
      return null;
    }
    if (!lastNameArr || !lastNameArr[0]) {
      return firstNameArr[0];
    }

    return firstNameArr[0] + lastNameArr[0];
  };

  const getFontSize = (size: string): string => {
    if (size === 'small') return 'text-avatar-extra-small font-bold';
    if (size === 'large') return 'text-avatar-large';
    if (size === 'extra-large') return 'text-avatar-extra-large';
    return 'text-avatar-default';
  };

  useEffect(() => {
    if (size) {
      setFontSize(getFontSize(size));
    }
  }, [setFontSize, size]);

  const jsxStyle =
    staticBackground && backgroundColor ? { backgroundColor } : undefined;

  return (
    <div
      className={`${sizeStyles} ${
        !staticBackground && memoizedValue
      } rounded-full inline-flex items-center justify-center font-open-sans avatar-initials capitalize text-white font-semibold ${className}`}
      style={jsxStyle}
      {...rest}
    >
      <span
        aria-label="User Initials"
        aria-describedby={`User Initials for ${name.firstName} ${name.lastName}`}
        className={`${fontSize}`}
      >
        {getInitials(name)}
      </span>
    </div>
  );
};
